import React from 'react'
import {useContext} from 'react'
import clsx from 'clsx'
import {KTIcon} from '@/_metronic/helpers'
import {ProductContext} from '../ProductContext'
import {ReactCountryFlag} from 'react-country-flag'
import BrandIcon from '@/app/modules/p2c/_components/icons/BrandIcon'
import {RouteTabs} from '@/components/tabs/RouteTabs'
import {useIntl} from 'react-intl'

const Header = () => {
  const { product } = useContext(ProductContext)
  const intl = useIntl()

  if (!product) {
    return (<></>)
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-body pt-9 pb-0'>
        <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
          <div className='me-7 mb-4'>
            <div className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative'>
              {product.cover ? (
                <img src={product.cover} alt={product.name + ' cover'} className='w-100' />
              ) : (
                <img src="/media/svg/files/blank-image.svg" alt={product.name + ' cover'} className='w-100' />
              )}
            </div>
          </div>

          <div className='flex-grow-1'>
            <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
              <div className='d-flex flex-column'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='text-gray-800 fs-2 fw-bolder me-1'>
                    {product.name}
                  </span>
                  { product.alias && (
                    <span className='d-flex align-items-center text-danger ms-1' title={product.alias}>
                      <KTIcon iconName="information" className="fs-1 pe-2 text-danger"/>
                      <b className="fs-2">ALIAS</b>
                    </span>
                  )}

                </div>

                <div className='d-flex flex-wrap fw-bold fs-6 mb-4 pe-2'>
                  <span className='d-flex align-items-center text-gray-400 mb-2 me-5' title="Category">
                    <KTIcon iconName="category" className="fs-3 pe-2"/>
                    {intl.formatMessage({id: `P2C.PRODUCT_TYPE_${product.type}`})}
                  </span>

                  <span className='d-flex align-items-center text-gray-400 mb-2 me-5' title="Status">
                    <KTIcon
                      iconName={`toggle-${product.status === 'PUBLISHED' ? 'on' : 'off'}-circle`}
                      className={clsx("fs-1 text-primary pe-2", {
                        'text-success': product.status === 'PUBLISHED',
                        'text-primary': product.status === 'DRAFT',
                        'text-danger': product.status === 'DISABLED'
                      })}
                    />
                    {intl.formatMessage({id: `P2C.PRODUCT_STATUS_${product.status}`})}
                  </span>

                  {product.brand && (
                    <span className='d-flex align-items-center text-gray-400 mb-2 me-5' title="Brand">
                      <BrandIcon brand={product.brand} className="fs-1 pe-2" />
                      {product.brand.name}
                    </span>
                  )}
                  

                </div>
              </div>
            </div>

            <div className='d-flex flex-wrap flex-stack'>
              <div className='d-flex flex-column flex-grow-1 pe-8'>
                <div className='d-flex flex-wrap'>
                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <ReactCountryFlag
                        countryCode={product.region ?? ''}
                        style={{ fontSize: '1.5em' }}
                        className='h-20px rounded-1 ms-2 pe-2'
                      />
                      <div className="fs-2 fw-bold" >{product.region}</div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Region</div>
                  </div>

                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="price-tag" className="fs-1 pe-2"/>
                      <div className="fs-2 fw-bold" >{product.credits}</div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Price in Credits</div>
                  </div>

                  {product.faceAmount && (
                    <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                      <div className="d-flex align-items-center">
                        <KTIcon iconName="euro" className="fs-1 pe-2"/>
                        <div className="fs-2 fw-bold" >{product.faceAmount} {product.faceCurrency}</div>
                      </div>
                      <div className="fw-semibold fs-6 text-gray-500">Face amount</div>
                    </div>
                  )}

                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="purchase" className="fs-1 pe-2"/>
                      <div className="fs-2 fw-bold" >{product.stock}</div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Internal stock</div>
                  </div>

                  {!product.alias && (
                    <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                      <div className="d-flex align-items-center">
                        <KTIcon iconName="purchase" className="fs-1 pe-2"/>
                        <div className="fs-2 fw-bold" >{intl.formatNumber(product.sold)}</div>
                      </div>
                      <div className="fw-semibold fs-6 text-gray-500">Sold</div>
                    </div>
                  )}

                </div>
              </div>

              <div className='d-flex align-items-center w-200px w-sm-300px flex-column mt-3'>
                <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                  <div className="d-flex border border-gray-300 border-dashed rounded px-4 me-6">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="like-2" className="fs-1 pe-2 text-danger"/>
                      <div className="fs-2 fw-bold" >{product.wishlisted}</div>
                    </div>
                  </div>

                  <div className="d-flex border border-gray-300 border-dashed rounded px-4 me-6">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="like" className="fs-1 pe-2 text-success"/>
                      <div className="fs-2 fw-bold" >{product.thumbs.thumbsUp}</div>
                    </div>
                  </div>

                  <div className="d-flex border border-gray-300 border-dashed rounded px-4 me-6">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="dislike" className="fs-1 pe-2 text-danger"/>
                      <div className="fs-2 fw-bold" >{product.thumbs.thumbsDown}</div>
                    </div>
                  </div>
                </div>

                <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                  <span className='fw-bold fs-6 text-gray-400'>Product Completion</span>
                  <span className='fw-bolder fs-6'>50%</span>
                </div>
                <div className='h-5px mx-3 w-100 bg-light mb-3'>
                  <div
                    className='bg-success rounded h-5px'
                    role='progressbar'
                    style={{width: '50%'}}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <RouteTabs
          tabs={[
            { label: 'Overview', route: `/p2c/product/${product._id}/overview` },
            ...(product.alias ? [] : [{
              label: 'Suppliers',
              route: `/p2c/product/${product._id}/suppliers`
            }]),
            { label: 'Reports', route: `/p2c/product/${product._id}/reports` }
          ]}
        />

      </div>
    </div>
  )
}

export {Header}
